import React, { FC } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { routes } from "@app/routing";
import { GIS_NO_ORDER_UID } from "@app/utils/gisEntrySource";
import {
  GIS_GUEST_INTAKE_STEPS,
  GIS_GUEST_INTAKE_STEPS_NO_ORDER,
} from "@app/service/intakeForm";
import {
  GisGuestProvider,
  IntakeFormProvider,
  guestIntakeStateCacheKey,
  readGuestGisSession,
} from "@app/pages/private/scheduleAGeneticInformationSession/context";

/**
 * Layout for the anonymous order-lookup (Public_Entry_Order_Lookup) ONC intake
 * + custom-scheduler routes. Mirrors the authenticated `OncUppGisTriageLayout`
 * (scoping `IntakeFormProvider` per order + test) and additionally provides the
 * guest identity (signed token + matched-order test type / status) read from
 * sessionStorage, so the shared intake/schedule components route through the
 * NonAuth resolvers.
 *
 * The guest session is written by the lookup funnel just before it navigates
 * here. If it's missing (e.g. a direct visit or a page reload, which clears the
 * funnel's in-memory state), there's no token to authorize the flow, so we
 * bounce back to the lookup start.
 */
const GuestGisIntakeLayout: FC = () => {
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();

  const guestSession =
    orderUidPublic && testUid
      ? readGuestGisSession(orderUidPublic, testUid)
      : null;

  if (!guestSession?.token) {
    return <Navigate to={routes.publicGisOrderLookup} replace />;
  }

  const stateCacheKey = guestIntakeStateCacheKey(
    orderUidPublic ?? "",
    testUid ?? ""
  );

  // No-order guests (matched patient, no GIS-eligible order) run the extended
  // 11-step intake sequence (select-test / cancer-type / cancer-stage) just like
  // the authenticated no-order flow, so the provider must know it's no-order.
  const isNoOrder = orderUidPublic === GIS_NO_ORDER_UID;

  return (
    <GisGuestProvider
      token={guestSession.token}
      testType={guestSession.testType}
      status={guestSession.status}
      counselingIntent={guestSession.counselingIntent}
      limsCaseId={guestSession.limsCaseId}
    >
      <IntakeFormProvider
        key={stateCacheKey}
        stateCacheKey={stateCacheKey}
        isNoOrder={isNoOrder}
        stepSequence={
          isNoOrder ? GIS_GUEST_INTAKE_STEPS_NO_ORDER : GIS_GUEST_INTAKE_STEPS
        }
      >
        <Outlet />
      </IntakeFormProvider>
    </GisGuestProvider>
  );
};

export default GuestGisIntakeLayout;
